import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaInfoCircle as Info } from 'react-icons/fa';

import { ActionButton, Field, Notice, PanelHeader, Section, Segment } from './formPrimitives';
import { fetchRevisionHistory } from './listApi';
import {
  FOLLOW_COMPARE_KEYS,
  applyBirthAndExamAge,
  buildChangeSummary,
  buildPatientPayload,
  createInitialForm,
  formatDate,
  getSessionUser,
  historyRowToForm,
  inputsEqual,
  isBlank,
  isFollowupAction,
  isRecommendAction,
  pickLatestUsedLens
} from './v2Helpers';
import { numeric, saveLensRecommendResult } from '../../utils/lensRecommend';

function eyeLabel(eye) {
  if (eye === 'OU') return 'OD · OS';
  return eye || '—';
}

/**
 * 팔로우 탭
 * - 상단: 최초 최신 추천 렌즈 / 예측 1개월 vault / 실제 사용 렌즈(읽기 전용)
 * - 본문: 기본정보 + OPMR/각막 + 생체계측/CASIA (수기 입력)
 * - 실측 vault
 * - action_type=followup 으로 create/update(후속 INSERT)
 *
 * used_lens 정책:
 * - 최초 탭에서만 기입·수정. 팔로우는 최신 used_lens 를 참조 표시만 한다.
 * - 저장 시 followup 행에 기존 used_lens 값을 그대로 복사한다(여기서 변경하지 않음).
 */
export default function LensFollowTab({ chartNo, eye, caseId = '', recommendSnapshot }) {
  const [form, setForm] = useState(() => {
    const initial = createInitialForm();
    return {
      ...initial,
      case_id: caseId || '',
      chart_no: chartNo || '',
      eye: eye || 'OD'
    };
  });
  const [baselineSnapshot, setBaselineSnapshot] = useState(null);
  const [baselineMeta, setBaselineMeta] = useState({
    recommendedSize: recommendSnapshot?.size_mm ?? '—',
    recommendedSphere: recommendSnapshot?.sphere ?? '—',
    predictedVault1Day: recommendSnapshot?.predicted_vault_1day_um ?? null,
    predictedVault1Month: recommendSnapshot?.predicted_vault_1month_um ?? null
  });
  const [revisions, setRevisions] = useState([]);
  const [historyTick, setHistoryTick] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [error, setError] = useState('');
  const formOwnedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!chartNo) return;
      const user = getSessionUser();
      if (!user?.hospitalId) return;

      const endDt = formatDate();
      const start = new Date();
      start.setFullYear(start.getFullYear() - 2);

      try {
        const rows = await fetchRevisionHistory({
          chartNo,
          hospitalId: user.hospitalId,
          startDt: formatDate(start),
          endDt,
          caseId: caseId || undefined
        });
        if (cancelled) return;

        const eyeRows = rows.filter((row) => (row.eye || 'OD') === (eye || 'OD'));
        const caseRows = caseId
          ? eyeRows.filter((row) => String(row.caseId || row.case_id || '') === String(caseId))
          : eyeRows;
        const followRows = caseRows.filter(isFollowupAction);
        const recommendRows = caseRows.filter(isRecommendAction);
        setRevisions(followRows);

        if (formOwnedRef.current) return;

        const latestRecommend = recommendRows[0];
        const latestFollow = followRows[0];
        const usedLens = pickLatestUsedLens(caseRows);

        let nextForm = createInitialForm();
        nextForm.case_id = caseId || '';
        nextForm.chart_no = chartNo || '';
        nextForm.eye = eye || 'OD';

        if (latestRecommend) {
          nextForm = {
            ...historyRowToForm(latestRecommend, nextForm),
            exam_date: formatDate(),
            actual_vault_1day: '',
            actual_vault_1month: '',
            postop_va: '',
            follow_note: ''
          };
        }

        if (latestFollow) {
          const followForm = historyRowToForm(latestFollow);
          nextForm = {
            ...nextForm,
            ...followForm,
            // 추천 렌즈 참조는 recommend 최신 유지
            lens_size: latestRecommend?.resultLensSize ?? nextForm.lens_size,
            lens_sphere: latestRecommend?.resultLensSphere != null
              ? String(latestRecommend.resultLensSphere)
              : nextForm.lens_sphere
          };
          setBaselineSnapshot(followForm);
        } else {
          setBaselineSnapshot(null);
        }

        nextForm = {
          ...nextForm,
          ...usedLens,
          // 수술일은 렌즈추천 탭이 소스. 없으면 경과관찰 이력 값 유지.
          surgery_date:
            latestRecommend?.surgeryDate
            || latestRecommend?.surgery_date
            || nextForm.surgery_date
            || '',
          case_id: caseId || nextForm.case_id || ''
        };

        setForm(nextForm);
        setBaselineMeta({
          recommendedSize:
            latestRecommend?.resultLensSize
            ?? recommendSnapshot?.size_mm
            ?? '—',
          recommendedSphere:
            latestRecommend?.resultLensSphere
            ?? recommendSnapshot?.sphere
            ?? '—',
          predictedVault1Day:
            latestRecommend?.predictedVault1DayUm
            ?? recommendSnapshot?.predicted_vault_1day_um
            ?? null,
          predictedVault1Month:
            latestRecommend?.predictedVault1MonthUm
            ?? recommendSnapshot?.predicted_vault_1month_um
            ?? null
        });
      } catch (err) {
        if (!cancelled) {
          console.error('[LensFollowTab] load failed', err);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [chartNo, eye, caseId, recommendSnapshot, historyTick]);

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setSaveMessage('');
    setError('');
  };

  const updateExamDate = (value) => {
    setForm((current) => applyBirthAndExamAge(current, current.birth_date, value));
  };

  const axisLabel = form.orientation === '수직' ? 'V' : 'H';
  const computedAge = Number.isFinite(Number(form.age_years)) ? Number(form.age_years) : null;

  const vaultDiff = useMemo(() => {
    const predicted = Number(baselineMeta.predictedVault1Month);
    const actual = Number(form.actual_vault_1month);
    if (!Number.isFinite(predicted) || !Number.isFinite(actual)) return null;
    return Math.round(actual - predicted);
  }, [baselineMeta.predictedVault1Month, form.actual_vault_1month]);

  const revisionView = revisions.map((row, index, all) => {
    const newer = all[index];
    const older = all[index + 1];
    return {
      key: row.examId || `${row.examDt}-${index}`,
      dt: row.examDt || '',
      summary: buildChangeSummary(
        older ? historyRowToForm(older) : null,
        historyRowToForm(newer),
        FOLLOW_COMPARE_KEYS
      )
    };
  });

  const handleSave = async () => {
    if (isBlank(form.chart_no)) {
      setError('차트번호가 없습니다.');
      return;
    }
    if (isBlank(form.birth_date)) {
      setError('생년월일을 입력해주세요.');
      return;
    }
    if (baselineSnapshot && inputsEqual(baselineSnapshot, form, FOLLOW_COMPARE_KEYS)) {
      setSaveMessage('입력값이 이전과 동일하여 저장하지 않았습니다.');
      return;
    }

    const user = getSessionUser();
    if (!user?.userNo) {
      setError('로그인 사용자 정보가 없어 저장하지 못했습니다.');
      return;
    }

    setLoading(true);
    setError('');
    setSaveMessage('');

    try {
      const revisionKind = baselineSnapshot ? 'update' : 'create';
      const patientPayload = buildPatientPayload(form, computedAge);
      const lensPayload = {
        size_mm: numeric(form.lens_size, null),
        sphere: numeric(form.lens_sphere, null),
        cylinder: numeric(form.opmr_cylinder, null)
      };

      const result = {
        actual_vault_1day_um: numeric(form.actual_vault_1day, null),
        actual_vault_1month_um: numeric(form.actual_vault_1month, null),
        postop_va: String(form.postop_va || '').trim() || null,
        surgery_date: form.surgery_date || null,
        follow_note: form.follow_note || null,
        exam_date: form.exam_date || null,
        predicted_vault_1day_um: baselineMeta.predictedVault1Day,
        predicted_vault_1month_um: baselineMeta.predictedVault1Month,
        recommended_lens: {
          size_mm: baselineMeta.recommendedSize !== '—' ? Number(baselineMeta.recommendedSize) : null,
          sphere: baselineMeta.recommendedSphere !== '—' ? Number(baselineMeta.recommendedSphere) : null
        },
        size_mm: baselineMeta.recommendedSize !== '—' ? Number(baselineMeta.recommendedSize) : null,
        sphere: baselineMeta.recommendedSphere !== '—' ? Number(baselineMeta.recommendedSphere) : null
      };

      await saveLensRecommendResult({
        userNo: user.userNo,
        hospitalId: user.hospitalId,
        actionType: 'followup',
        revisionKind,
        caseId: caseId || form.case_id || undefined,
        form: {
          ...form,
          case_id: caseId || form.case_id || undefined,
          revision_kind: revisionKind,
          age_group: form.age_group
        },
        patientPayload,
        lensPayload,
        result,
        sizeCompare: [],
        similarCases: [],
        warnings: []
      });

      formOwnedRef.current = true;
      setBaselineSnapshot({ ...form });
      setHistoryTick((tick) => tick + 1);
      setSaveMessage(
        revisionKind === 'create'
          ? '경과관찰 데이터가 저장되었습니다.'
          : '경과관찰 수정분이 새 이력으로 저장되었습니다.'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : '경과관찰 저장 실패');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lens-v2-tab-root">
      <section className="lens-v2-summary-card" style={{ marginBottom: 12 }}>
        <div className="lens-v2-history-title">
          <h3>추천 · 사용 렌즈 요약</h3>
          <span className="lens-v2-pill">렌즈추천 참조</span>
        </div>
        <div className="lens-v2-summary-grid">
          <div className="lens-v2-summary-item">
            <span>추천 렌즈 (최신)</span>
            <strong>
              {baselineMeta.recommendedSize}
              {baselineMeta.recommendedSize !== '—' ? ' mm' : ''}
            </strong>
            <span style={{ marginTop: 6 }}>
              Sphere {baselineMeta.recommendedSphere}
              {baselineMeta.recommendedSphere !== '—' ? ' D' : ''}
            </span>
          </div>
          <div className="lens-v2-summary-item">
            <span>예측 1개월 vault</span>
            <strong>
              {baselineMeta.predictedVault1Month != null
                ? `${Math.round(Number(baselineMeta.predictedVault1Month))} µm`
                : '—'}
            </strong>
          </div>
          <div className="lens-v2-summary-item">
            <span>실제 사용 렌즈</span>
            {form.used_lens_size || form.used_lens_sphere || form.surgery_date ? (
              <>
                <strong>
                  {form.used_lens_size ? `${form.used_lens_size} mm` : '—'}
                </strong>
                <span style={{ marginTop: 6 }}>
                  Sphere {form.used_lens_sphere || '—'}
                  {form.used_lens_sphere ? ' D' : ''}
                  {form.used_lens_cylinder
                    ? ` · Cylinder ${form.used_lens_cylinder} D`
                    : ''}
                </span>
                <span style={{ marginTop: 6 }}>
                  수술일 {form.surgery_date || '—'}
                </span>
              </>
            ) : (
              <>
                <strong>—</strong>
                <span style={{ marginTop: 6 }}>렌즈추천 탭에서 입력하세요.</span>
              </>
            )}
          </div>
        </div>
        {vaultDiff != null ? (
          <p style={{ margin: '12px 0 0', color: '#475569', fontSize: 13 }}>
            1개월 vault 차이 (실측 − 예측): {vaultDiff > 0 ? '+' : ''}
            {vaultDiff} µm
          </p>
        ) : null}
      </section>

      <div className="lens-input-column lens-panel" style={{ width: '100%', maxWidth: '100%' }}>
        <PanelHeader title="경과관찰 검사 입력" label="FOLLOW" />
        <div className="lens-panel-body">
          <Section title="기본 정보">
            <div className="lens-grid">
              <Field
                label="검사일 (경과관찰 방문일)"
                type="date"
                value={form.exam_date}
                onChangeText={updateExamDate}
                hint="나이 계산 기준일"
              />
              <Field label="차트번호" value={form.chart_no} readOnly />
              <Segment
                label="성별"
                value={form.gender}
                options={['F', 'M']}
                onChange={(v) => update('gender', v)}
                disabled
              />
              <Field
                label="생년월일"
                type="date"
                value={form.birth_date}
                readOnly
              />
              <Field label="나이" value={form.age_years} readOnly />
              <Field label="연령대" value={form.age_group} readOnly />
              <Field label="Eye" value={eyeLabel(form.eye)} readOnly />
              <Segment
                label="삽입 방향"
                value={form.orientation}
                options={['수평', '수직']}
                onChange={(v) => update('orientation', v)}
                disabled
              />
            </div>
          </Section>

          <Section title="경과관찰">
            <div className="lens-grid">
              <Field
                label="실측 1일 vault (µm)"
                value={form.actual_vault_1day}
                onChangeText={(v) => update('actual_vault_1day', v)}
                numeric
              />
              <Field
                label="실측 1개월 vault (µm)"
                value={form.actual_vault_1month}
                onChangeText={(v) => update('actual_vault_1month', v)}
                numeric
              />
              <Field
                label="수술 후 시력"
                value={form.postop_va}
                onChangeText={(v) => update('postop_va', v)}
              />
              <Field
                label="비고"
                value={form.follow_note}
                onChangeText={(v) => update('follow_note', v)}
              />
            </div>
          </Section>

          <Section title="OPMR / 각막">
            <div className="lens-grid">
              <Field label="OPMR Sphere" value={form.opmr_sphere} onChangeText={(v) => update('opmr_sphere', v)} numeric />
              <Field label="OPMR Cylinder" value={form.opmr_cylinder} onChangeText={(v) => update('opmr_cylinder', v)} numeric />
              <Field label="OPMR Axis" value={form.opmr_axis} onChangeText={(v) => update('opmr_axis', v)} numeric />
              <Field label="K1" value={form.k1} onChangeText={(v) => update('k1', v)} numeric />
              <Field label="K2" value={form.k2} onChangeText={(v) => update('k2', v)} numeric />
              <Field label="K Axis" value={form.k_axis} onChangeText={(v) => update('k_axis', v)} numeric />
            </div>
          </Section>

          <div className="lens-compact-pair">
            <Section title="생체계측">
              <div className="lens-grid">
                <Field label="ACD" value={form.acd_preop} onChangeText={(v) => update('acd_preop', v)} numeric />
                <Field label="WTW" value={form.wtw} onChangeText={(v) => update('wtw', v)} numeric />
                <Field label="CT" value={form.ct} onChangeText={(v) => update('ct', v)} numeric />
                <Field label="Expected SE" value={form.exp_se} onChangeText={(v) => update('exp_se', v)} numeric />
              </div>
            </Section>
            <Section title={`CASIA2 (${form.orientation})`}>
              <div className="lens-grid">
                <Field label={`${axisLabel} ACW`} value={form.casia_acw} onChangeText={(v) => update('casia_acw', v)} numeric />
                <Field label={`${axisLabel} CLR`} value={form.casia_clr} onChangeText={(v) => update('casia_clr', v)} numeric />
                <Field label={`${axisLabel} ATA`} value={form.casia_ata} onChangeText={(v) => update('casia_ata', v)} numeric />
                <Field label={`${axisLabel} ACD`} value={form.casia_acd} onChangeText={(v) => update('casia_acd', v)} numeric />
              </div>
            </Section>
          </div>

          <Section title="저장">
            <div className="lens-recommend-action-panel">
              <div className="lens-recommend-action-copy">
                <strong>경과관찰 검사값 저장</strong>
              </div>
              <div className="lens-actions">
                <ActionButton
                  label={loading ? '저장 중…' : '경과관찰 저장'}
                  onClick={handleSave}
                  primary
                  disabled={loading}
                />
              </div>
            </div>
            {error ? <Notice tone="bad" icon={<Info size={15} color="#b91c1c" />}>{error}</Notice> : null}
            {saveMessage ? <Notice tone="info" icon={<Info size={15} color="#1d4ed8" />}>{saveMessage}</Notice> : null}
          </Section>
        </div>
      </div>

      <section className="lens-v2-history-card" style={{ marginTop: 12 }}>
        <div className="lens-v2-history-title">
          <h3>수정이력</h3>
          <span className="lens-v2-pill">{revisionView.length}건</span>
        </div>
        {revisionView.length ? (
          <div className="lens-v2-table-wrap">
            <table className="lens-v2-table">
              <thead>
                <tr>
                  <th>수정일시</th>
                  <th>변경내용</th>
                </tr>
              </thead>
              <tbody>
                {revisionView.map((row) => (
                  <tr key={row.key}>
                    <td>{row.dt || '—'}</td>
                    <td>
                      <p className="lens-v2-change-summary">{row.summary}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="lens-v2-empty">
            아직 경과관찰 이력이 없습니다. 「경과관찰 저장」 시 이력이 생성됩니다.
          </div>
        )}
      </section>
    </div>
  );
}
